import React, { useState, useEffect } from 'react';
import {
    makeStyles, tokens,
    Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
    Button,
} from '@fluentui/react-components';
import { iconMap } from '../../theme/icons';
import SelectionBox from '../components/blocks/SelectionBox';

import pic1 from '../../assets/img/icons/dureles/1.jpg';
import pic2 from '../../assets/img/icons/dureles/2.jpg';
import pic3 from '../../assets/img/icons/dureles/3.jpg';
import pic4 from '../../assets/img/icons/dureles/4.jpg';
import pic5 from '../../assets/img/icons/dureles/5.jpg';
import pic6 from '../../assets/img/icons/dureles/6.jpg';
import pic7 from '../../assets/img/icons/dureles/7.jpg';

const CloseIcon = iconMap.close;

export const doorOptions = [
    { image: pic1, value: 0, label: 'Be durelių / stalčių', description: 'Nėra fasado' },
    { image: pic2, value: 1, label: 'Durys (Kairė)', description: 'Vienos durys, atidaromos į kairę' },
    { image: pic3, value: 2, label: 'Durys (Dešinė)', description: 'Vienos durys, atidaromos į dešinę' },
    { image: pic4, value: 3, label: 'Durys (Dvigubos)', description: 'Dvejos durys, atidaromos į šonus' },
    { image: pic5, value: 4, label: 'Atverčiamos durys (Viršus)', description: 'Atsidarančios į viršų' },
    { image: pic6, value: 5, label: 'Atverčiamos durys (Apačia)', description: 'Atsidarančios į apačią' },
    { image: pic7, value: 6, label: 'Stalčius (vienas fasadas)', description: 'Vienas stalčiaus fasadas' },
];

// Pagal sekcijos faktinius matmenis (cm) nustatome, kurie durelių/stalčių tipai netinka.
// Be matmenų (sectionDims == null) nieko neribojame.
const isDoorOptionDisabled = (value, dims) => {
    if (!dims) return false;
    const { widthCm: w, heightCm: h, depthCm: d } = dims;
    switch (value) {
        case 1: // Durys (Kairė)
        case 2: // Durys (Dešinė)
            return !(h > 10 && h < 275 && w > 8 && w < 65);
        case 3: // Durys (Dvigubos)
            return !(h > 10 && h < 275 && w > 16 && w < 130);
        case 4: // Atverčiamos durys (Viršus)
        case 5: // Atverčiamos durys (Apačia)
            return !(h > 15 && h < 80 && w > 15 && w < 130);
        case 6: // Stalčius
            return !(h > 8 && h < 85 && w > 15 && w < 120 && d > 20);
        default: // 0 - Be durelių / stalčių
            return false;
    }
};

const useStyles = makeStyles({
    surface: { maxWidth: '37.5rem', width: '100%' },
    content: { paddingTop: tokens.spacingVerticalM },
    // CSS grid + repeat(auto-fill,...) su fiksuoto pločio SelectionBox (type=column,
    // justifySelf:start) palikdavo tuščius grid langelius kaip pilno pločio "tarpus",
    // kai stulpelių daugiau nei realių elementų (žr. /admin/bugs "Ta pati grid
    // problema, nenaudojam grid" — bendra taisyklė visiems dialogams). flex+wrap+gap
    // vietoj grid — kiekvienas elementas užima tik savo turinio plotį.
    grid: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
    },
    actions: { gap: tokens.spacingHorizontalS },
    actionBtn: { flex: 1 },
});

const OuterSelectingDialog = ({ open, onClose, selectedShelfInfo, doorColors, onSelectOption, selectedOption, sectionDims }) => {
    const s = useStyles();
    const initialValue = (() => {
        if (selectedShelfInfo && selectedShelfInfo.cabinetIndex !== null && selectedShelfInfo.shelfIndex !== null) {
            const key = selectedShelfInfo.furnitureId
                ? `${selectedShelfInfo.furnitureId}-${selectedShelfInfo.cabinetIndex}-${selectedShelfInfo.shelfIndex}`
                : `${selectedShelfInfo.cabinetIndex}-${selectedShelfInfo.shelfIndex}`;
            return (doorColors && doorColors[key]) || selectedOption || 0;
        }
        return selectedOption || 0;
    })();

    const [tempValue, setTempValue] = useState(initialValue);

    useEffect(() => {
        if (open) setTempValue(initialValue);
    }, [open]);

    const handleConfirm = () => {
        onSelectOption(tempValue);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(_e, data) => { if (!data.open) onClose(); }}>
            <DialogSurface id="dialog-outer-selecting" className={s.surface}>
                <DialogBody>
                    <DialogTitle action={<Button appearance="subtle" icon={<CloseIcon />} aria-label="Uždaryti" onClick={onClose} />}>
                        Pasirinkite durelių / stalčių tipą
                    </DialogTitle>
                    <DialogContent className={s.content}>
                        <div className={s.grid}>
                            {doorOptions.map(option => {
                                const disabled = isDoorOptionDisabled(option.value, sectionDims);
                                return (
                                    <SelectionBox
                                        key={option.value}
                                        image={option.image}
                                        title={option.label}
                                        subTitle={option.description}
                                        selected={option.value === tempValue}
                                        onClick={() => setTempValue(option.value)}
                                        disabled={disabled}
                                    />
                                );
                            })}
                        </div>
                    </DialogContent>
                    <DialogActions className={s.actions}>
                        <Button size="large" appearance="outline" onClick={onClose} className={s.actionBtn}>
                            Atšaukti
                        </Button>
                        <Button size="large" appearance="primary" onClick={handleConfirm} className={s.actionBtn}>
                            Pasirinkti
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default OuterSelectingDialog;
